import React from 'react';
import {
  Building2, Users, Briefcase, ShieldCheck, Wallet, Lock,
  Heart, Sparkles,
} from 'lucide-react';
import useGameStore from '../../store/gameStore';
import { formatMoney } from '../../utils/format';
import { OFFICE_UPGRADES, isOfficeUnlocked } from '../../data/officeUpgrades';
import OfficeScene from './OfficeScene';
import { useTranslation } from '../../i18n';

/**
 * HQ informative panel — combines a "headquarters office" infographic
 * scene with live game data (support staff, office upgrades owned,
 * monthly cost, satisfaction boost). Locked until the office is
 * unlocked (3+ support OR inv-office-01).
 *
 * Design (28/05/2026 v2) :
 *   - Locked state : neutral "blueprint" view with the unlock condition
 *     spelled out
 *   - Unlocked state : SVG illustration of the office floor (cubicles,
 *     break room, fountain area…) with INLAY badges showing which
 *     upgrades are active + a sidebar with KPIs (headcount, monthly
 *     cost, satisfaction boost) + a strip listing all upgrades with
 *     their per-upgrade visual.
 *   - Office upgrades arrive via opportunistic email proposals — see
 *     officeSlice.maybeFireOfficeProposal.
 */

export default function HQOfficePanel() {
  const { t } = useTranslation();
  const state = useGameStore();
  const unlocked = isOfficeUnlocked(state);
  const owned = state.officeUpgrades || {};
  const ownedCount = Object.keys(owned).length;
  const monthlyCost = typeof state.getOfficeMonthlyCost === 'function'
    ? state.getOfficeMonthlyCost() : 0;
  const satBoost = typeof state.getOfficeSatisfactionBoost === 'function'
    ? state.getOfficeSatisfactionBoost() : 0;
  const supportCount = Object.keys(state.hiredSupport || {}).length;

  if (!unlocked) {
    return (
      <div
        className="bg-ink-800 rounded-lg border border-ink-600 p-4 space-y-2"
        data-testid="hq-office-panel-locked"
      >
        <div className="flex items-center gap-2">
          <Lock className="w-4 h-4 text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-300">{t('hq_locked_title')}</h3>
        </div>
        <div className="bg-ink-700/30 rounded-md border border-ink-600 p-3 flex flex-col md:flex-row gap-3">
          {/* Real Nano Banana image of the empty office (phase 0) */}
          <div className="md:w-48 flex-shrink-0 opacity-60">
            <OfficeScene unlocked={false} supportCount={supportCount} upgradeCount={ownedCount} />
          </div>
          <div className="flex-1 text-xs text-slate-400 leading-relaxed">
            <p>{t('hq_locked_body').replace('{threshold}', '3')}</p>
            <p className="mt-2 text-slate-500">
              <span className="inline-flex items-center gap-1 bg-ink-800 px-2 py-0.5 rounded font-mono"><Users className="w-3 h-3" /> {supportCount}/3</span>
              {supportCount < 3 && <span className="ml-2 text-warn">{t('hq_locked_more_to_hire').replace('{count}', String(3 - supportCount))}</span>}
              {supportCount >= 3 && <span className="ml-2 text-money">{t('hq_locked_unlock_imminent')}</span>}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-ink-800 rounded-lg border border-ink-600 p-4 space-y-3" data-testid="hq-office-panel-unlocked">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
            <Building2 className="w-4 h-4 text-accent-foreground" /> {t('hq_unlocked_title')}
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            {t('hq_unlocked_body')}
          </p>
        </div>
        <div className="text-right flex-shrink-0">
          <p className="text-2xl font-black font-mono text-foreground" data-testid="hq-office-upgrade-count">
            {ownedCount}<span className="text-base text-slate-500">/{OFFICE_UPGRADES.length}</span>
          </p>
          <p className="text-[10px] text-slate-500 font-mono uppercase tracking-wider">{t('hq_upgrades_installed')}</p>
        </div>
      </div>

      {/* Visual + KPI sidebar */}
      <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-3">
        {/* Nano Banana evolving image of the office.
            (28/05/2026 v2 — replaces the static SVG schematic) */}
        <OfficeScene
          unlocked={true}
          supportCount={supportCount}
          upgradeCount={ownedCount}
        />

        {/* KPI sidebar */}
        <div className="flex md:flex-col gap-2 flex-row flex-wrap md:w-44">
          <KpiBox
            label={t('hq_kpi_support')}
            value={supportCount}
            icon={Briefcase}
            color="text-foreground"
            testId="hq-kpi-support"
          />
          <KpiBox
            label={t('hq_kpi_monthly')}
            value={`${formatMoney(monthlyCost)}/m`}
            icon={Wallet}
            color="text-danger"
            testId="hq-kpi-monthly"
          />
          <KpiBox
            label={t('hq_kpi_satisfaction')}
            value={`+${satBoost} pt${satBoost > 1 ? 's' : ''}`}
            icon={Heart}
            color="text-money"
            testId="hq-kpi-sat-boost"
          />
        </div>
      </div>

      {/* Upgrade list */}
      <div className="border-t border-ink-600 pt-3">
        <p className="text-[10px] uppercase tracking-wider text-slate-500 font-mono mb-2 flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> {t('hq_equipment_section')}
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
          {OFFICE_UPGRADES.map(upg => {
            const owned_ = !!owned[upg.id];
            return (
              <div
                key={upg.id}
                className={`flex items-center gap-2 rounded-md border px-2.5 py-1.5 text-[11px] ${
                  owned_
                    ? 'bg-money/10 border-money/40 text-foreground'
                    : 'bg-ink-700/30 border-ink-600 text-slate-500'
                }`}
                data-testid={`hq-upgrade-${upg.id}`}
              >
                <span className="text-base">{upg.icon}</span>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold truncate ${owned_ ? 'text-foreground' : ''}`}>
                    {upg.name}
                  </p>
                  <p className={`text-[10px] ${owned_ ? 'text-money' : 'text-slate-500'}`}>
                    {owned_
                      ? `+${upg.satisfactionBoost} sat / ${formatMoney(upg.chargeMonthly)}/m`
                      : `${t('hq_upgrade_pending_mail')} · +${upg.satisfactionBoost} sat`}
                  </p>
                </div>
                {owned_ && <ShieldCheck className="w-3 h-3 text-money flex-shrink-0" />}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

const KpiBox = ({ label, value, icon: Icon, color, testId }) => (
  <div className="flex-1 rounded-md border border-ink-600 bg-ink-700/30 p-2.5 min-w-[120px]" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] text-slate-500 uppercase tracking-wider mb-1">
      <Icon className="w-3 h-3" /> {label}
    </div>
    <p className={`text-base font-bold font-mono ${color}`}>{value}</p>
  </div>
);
